import React from 'react';
import { Box, Icon, IconButton, TextInput } from '@fullstory/components';
import { useAsyncFilter, useFilter } from './useFilter';
import { intl } from '@fullstory/intl';

export interface SearchFilterProps extends React.ComponentProps<typeof TextInput> {
  filter: string;
  setFilter: (value: string) => void;
  placeholder: string;
  testId?: string;
  iconSize?: React.ComponentProps<typeof Icon>['size'];
}

export const SearchFilter: React.FC<SearchFilterProps> = ({
  filter,
  setFilter,
  placeholder,
  testId,
  iconSize,
  ...props
}) => (
  <TextInput
    data-component="BulkActionTableSearchFilter"
    testId={testId}
    value={filter}
    onChange={e => setFilter(e.target.value)}
    placeholder={placeholder}
    leftAdornment={
      <Box marginLeft={12}>
        <Icon name="search" size={iconSize} />
      </Box>
    }
    rightAdornment={
      filter !== '' && (
        <Box marginRight={12}>
          <IconButton
            title={intl.formatMessage({ defaultMessage: 'Clear', id: '/GCoTA' })}
            iconName="close"
            onClick={() => setFilter('')}
          />
        </Box>
      )
    }
    {...props}
  />
);

/**
 * Hook to create a search filter.
 * Consumers of BulkActionTable should use this to
 * create a search filter and pass it into the filters prop.
 * @param key React key used for rendering.
 * @param predicate Function to filter a single row. Returns true if the row should be rendered given the filter, false if not.
 */
export function useSearchFilter<R>(
  key: string,
  placeholder: string,
  predicate: (row: R, filter: string) => boolean,
) {
  return useFilter<R, string>(
    key,
    '',
    predicate,
    React.useCallback(
      props => <SearchFilter {...props} placeholder={placeholder} />,
      [placeholder],
    ),
  );
}

/**
 * Hook to create an async search filter.
 * Consumers of BulkActionTable should use this to
 * create a search filter and pass it into the filters prop.
 * Use this if you need to make a network call (for example) to do your row filtering.
 * @param key React key used for rendering.
 * @param filterRows Function to asynchronously filter the set of rows given the filter string.
 */
export function useAsyncSearchFilter<R>(
  key: string,
  placeholder: string,
  filterRows: (rows: R[], filter: string) => Promise<R[]>,
) {
  return useAsyncFilter<R, string>(
    key,
    '',
    filterRows,
    React.useCallback(
      props => <SearchFilter {...props} placeholder={placeholder} />,
      [placeholder],
    ),
  );
}
